import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon'
import { cn } from '#app/utils/misc'

export interface AudiencePathItem {
	step?: string
	label?: string
	eyebrow?: string
	title: string
	description: string
	href?: string
	ctaLabel?: string
	note?: string
	icon?: string
}

export interface AudiencePathsProps {
	eyebrow?: string
	title?: string
	subtitle?: string
	items?: AudiencePathItem[]
	background?: 'default' | 'muted' | 'primary-light'
}

export function AudiencePaths({
	eyebrow,
	title,
	subtitle,
	items = [],
	background = 'muted',
}: AudiencePathsProps) {
	const bgClasses = {
		default: 'bg-background',
		muted: 'bg-transparent',
		'primary-light': 'bg-primary/5',
	}

	if (items.length === 0) return null

	return (
		<section
			className={cn(
				'relative px-6 py-24 sm:px-8 lg:px-12',
				bgClasses[background],
			)}
		>
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 -z-10"
				style={{
					background:
						background === 'muted'
							? 'linear-gradient(180deg, oklch(0.97 0.02 85 / 0.4) 0%, transparent 80%)'
							: undefined,
				}}
			/>
			<div className="mx-auto max-w-6xl">
				{(eyebrow || title || subtitle) && (
					<div className="mx-auto mb-10 max-w-3xl text-center">
						{eyebrow && (
							<p className="text-label text-muted-foreground">{eyebrow}</p>
						)}
						{title && <h2 className="text-title-2 mt-4">{title}</h2>}
						{subtitle && (
							<p className="text-muted-foreground mx-auto mt-5 max-w-2xl text-base leading-relaxed md:text-lg">
								{subtitle}
							</p>
						)}
					</div>
				)}

				<div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{items.map((item, index) => {
						const eyebrowParts = item.eyebrow
							?.split('·')
							.map((part) => part.trim())
						const step = item.step ?? eyebrowParts?.[0]
						const label = item.label ?? eyebrowParts?.[1]

						return (
							<article
								key={`${item.title}-${index}`}
								className="border-primary/10 hover:border-primary/25 group flex h-full flex-col rounded-2xl border bg-white/60 p-6 backdrop-blur-sm transition-colors"
							>
								{item.icon ? (
									<div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-full">
										<Icon name={item.icon as any} className="size-5" />
									</div>
								) : null}

								{step ? (
									<p
										className="font-mono text-[11px] tracking-[0.22em]"
										style={{ color: 'var(--gold-text)' }}
									>
										{step}
									</p>
								) : null}

								<div className="mt-4 space-y-3">
									{label ? (
										<p className="text-label text-muted-foreground">{label}</p>
									) : item.eyebrow ? (
										<p className="text-label text-muted-foreground">
											{item.eyebrow}
										</p>
									) : null}
									<h3 className="text-primary text-lg leading-snug font-semibold">
										{item.title}
									</h3>
									<p className="text-muted-foreground text-sm leading-relaxed">
										{item.description}
									</p>
									{item.note ? (
										<p className="text-foreground text-sm font-medium">
											{item.note}
										</p>
									) : null}
								</div>

								{item.href ? (
									<div className="mt-auto pt-2">
										<Link
											to={item.href}
											className="text-primary inline-flex items-center text-sm font-semibold"
										>
											{item.ctaLabel ?? 'Explorar'}
										</Link>
									</div>
								) : null}
							</article>
						)
					})}
				</div>
			</div>
		</section>
	)
}
